import cn from 'classnames';
import { useRouter } from 'next/router';
import some from 'lodash/some';
import {
  BodyWrapper,
  dataTableStyles,
  HeaderRow,
  HeaderWrapper,
  TableEmptyRow,
} from '@creditflowresearch/uilib/Table/DataTable';
import { IColumn } from '@creditflowresearch/uilib/Table/DataTable/interfaces/Column';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import Paginator from '@creditflowresearch/uilib/Paginator';
import MetaSchema from '@/creditflowresearch/ui/common/components/MetaSchema';
import getBwicsInventoryMetaSchema from '@/creditflowresearch/ui/components/bonds/getBwicsInventoryMetaSchema';
import { IAssetClass } from '@/creditflowresearch/domain/vo/IAssetClass';

import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import styles from '@/creditflowresearch/ui/components/bonds/common/BondTables/bondsTables.module.scss';

interface IInventoryProps {
  isFetching: boolean,
  isItemsPerPageVisible?: boolean,
  data: Record<string, any>,
  totalCount: number,
  sortBy: string,
  sortOrder: string,
  page: number,
  perPage: number,
  getHeaderColumns: ({ isNew }: { isNew: boolean }) => IColumn[],
  inventoryRow: any,
  tableFooterLabel: React.ReactNode,
  emptyMessage?: React.ReactNode,
  sort: (sortBy: string, sortOrder: string, currentAssetClassId?: IAssetClass['id']) => void,
  paginate: (selected: number, currentAssetClassId?: IAssetClass['id']) => void,
  itemsPerPage: (value: number, currentAssetClassId?: IAssetClass['id']) => void,
  withSchema?: boolean,
  currentAssetClass?: IAssetClass,
}

const Inventory = (props: IInventoryProps) => {
  const {
    data,
    totalCount,
    page,
    perPage,
    sortBy,
    sortOrder,
    withSchema = true,
    isFetching,
    emptyMessage,
    isItemsPerPageVisible,
    inventoryRow: InventoryRow,
    getHeaderColumns,
    currentAssetClass,
  } = props;
  const { pathname } = useRouter();

  const handlePageChange = (page: { selected: number }) => {
    props.paginate(page.selected, currentAssetClass?.id);
  };

  const handlePerPageChange = (perPage: { value: number, label: string }) => {
    props.itemsPerPage(perPage.value, currentAssetClass?.id);
  };

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    props.sort(sortBy, sortOrder, currentAssetClass?.id);
  };

  const tableClass = cn(
    dataTableStyles.dataTable,
    dataTableStyles.isHoverable,
    dataTableStyles.nowrapCells,
  );

  const emptyMessageClass = cn(
    dataTableStyles.emptyMessage,
    styles.emptyMessage,
    spacesStyles.ptxl,
    spacesStyles.pbxl,
  );

  const isNewColumnVisible = some(data, (row) => row.isNew === true);

  const headerColumns = getHeaderColumns({
    isNew: isNewColumnVisible,
  });

  return (
    <>
      {
        withSchema && (
          <MetaSchema schema={ getBwicsInventoryMetaSchema(pathname, 'Inventories', currentAssetClass?.abbreviation) } />
        )
      }
      <div className={ cn(dataTableStyles.dataTableContainer, styles.tableContainer) }>
        { isFetching && (
          <Spinner overlay />
        ) }

        <table className={ tableClass } data-test="inventoryTable">
          <HeaderWrapper>
            <HeaderRow
              sortBy={ sortBy }
              sortOrder={ sortOrder }
              onSortChange={ handleSortChange }
              columns={ headerColumns }
            />
          </HeaderWrapper>

          <BodyWrapper>
            { data?.length ? data.map((row, index) => (
              <InventoryRow
                /* eslint-disable-next-line react/no-array-index-key */
                key={ index }
                rowIndex={ index }
                row={ row }
                isNewColumnVisible={ isNewColumnVisible }
              />
            )) : (
              <TableEmptyRow
                columnsCount={ headerColumns.length }
                dataTest="inventoryTable"
                isFetching={ isFetching }
                message={ emptyMessage }
                className={ emptyMessageClass }
              />
            ) }
          </BodyWrapper>
        </table>

        <Paginator
          dataTest="bondsTableInventory"
          page={ page }
          perPage={ perPage }
          totalCount={ totalCount }
          onPageChange={ handlePageChange }
          onItemsPerPageChange={ handlePerPageChange }
          labelOfText={ props.tableFooterLabel }
          isItemsPerPageVisible={ isItemsPerPageVisible }
        />
      </div>
    </>
  );
};

export default Inventory;
